import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { useInfiniteList } from "@/hooks/useInfiniteList";
import { initials } from "@/lib/format";
import { SharedMailboxDialog } from "@/components/SharedMailboxDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Eye, EyeOff, Loader2, RefreshCw, Plus, Pencil, Trash2, Clock, RotateCcw, CalendarClock } from "lucide-react";

const PURPOSE_CLS = {
  shared: "border-sky-200 bg-sky-50 text-sky-700",
  room: "border-violet-200 bg-violet-50 text-violet-700",
  equipment: "border-emerald-200 bg-emerald-50 text-emerald-700",
};
const PURPOSE_LABEL = { shared: "shared.typeShared", room: "shared.typeRoom", equipment: "shared.typeEquipment" };

export default function SharedMailboxes() {
  const { isAdmin, can } = useAuth();
  const manage = can("sharedMailboxes.manage");
  const { t } = useLang();
  const [boxes, setBoxes] = useState([]);
  const [domains, setDomains] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [visFilter, setVisFilter] = useState("visible");
  const sort = useTableSort("displayName", "asc", "shared");
  const cols = useColumns("shared");
  const [loading, setLoading] = useState(true);
  const [rescanning, setRescanning] = useState(false);
  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit"
  const [dialogBox, setDialogBox] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  // Retained shared mailboxes (leavers awaiting auto-deletion) — extend / reactivate.
  const [retained, setRetained] = useState([]);
  const [extendFor, setExtendFor] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [reactivateFor, setReactivateFor] = useState(null);
  const [busyRet, setBusyRet] = useState(false);

  const load = () => api.get("/users/shared").then((r) => setBoxes(r.data)).finally(() => setLoading(false));
  const loadRetained = () => manage && api.get("/shared-mailboxes/retention").then((r) => setRetained(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    loadRetained();
    if (can("sharedMailboxes.manage")) api.get("/accepted-domains").then((r) => setDomains(r.data.domains || [])).catch(() => {});
  }, []);

  const doExtend = async () => {
    setBusyRet(true);
    try {
      await api.post(`/shared-mailboxes/retention/${encodeURIComponent(extendFor.identity)}/extend`, { days: Number(extendDays) || 30 });
      toast.success(`Aufbewahrung um ${Number(extendDays) || 30} Tage verlängert`);
      setExtendFor(null); await loadRetained();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyRet(false); }
  };
  const doReactivate = async () => {
    setBusyRet(true);
    try {
      const { data } = await api.post(`/shared-mailboxes/retention/${encodeURIComponent(reactivateFor.identity)}/reactivate`);
      toast.success(data?.licenseNeeded
        ? "Postfach wird reaktiviert — bitte weise dem Benutzer eine Lizenz zu, damit das Postfach bestehen bleibt."
        : "Postfach wird reaktiviert.");
      setReactivateFor(null); await Promise.all([loadRetained(), load()]);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusyRet(false); }
  };


  const rescan = async () => {
    setRescanning(true);
    try { const { data } = await api.post("/shared-mailboxes/rescan"); toast.success(t("shared.rescanned").replace("{n}", data.shared)); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setRescanning(false); }
  };
  const toggleVis = async (id, isVisible) => {
    setBoxes((bs) => bs.map((x) => x.id === id ? { ...x, isVisible } : x));
    try { await api.patch(`/shared-mailboxes/${id}/visibility`, { isVisible }); }
    catch (e) { toast.error(apiErr(e)); setBoxes((bs) => bs.map((x) => x.id === id ? { ...x, isVisible: !isVisible } : x)); }
  };
  const confirmDelete = async () => {
    setBusy(true);
    try { await api.delete(`/shared-mailboxes/${deleting.id}`); toast.success(t("shared.deleted")); setDeleting(null); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const boxPurpose = (u) => (u.userPurpose || "shared").toLowerCase();
  const filtered = useMemo(() => {
    const rows = boxes
      .filter((u) => `${u.displayName} ${u.userPrincipalName} ${u.mail || ""}`.toLowerCase().includes(q.toLowerCase()))
      .filter((u) => typeFilter === "all" || boxPurpose(u) === typeFilter)
      .filter((u) => visFilter === "all" || (visFilter === "visible" ? u.isVisible !== false : u.isVisible === false));
    return sort.sortRows(rows, {
      type: boxPurpose,
      isVisible: (u) => u.isVisible !== false ? 1 : 0,
    });
  }, [boxes, q, typeFilter, visFilter, sort]);
  const { visible, sentinelRef, hasMore, shown, total } = useInfiniteList(filtered, 50);
  const colCount = 3 + (isAdmin ? 1 : 0) + (manage ? 1 : 0);
  const csvHeaders = [
    { key: "displayName", label: t("shared.colMailbox") },
    ...(cols.show("mail") ? [{ key: "mail", label: t("shared.colEmail") }] : []),
    ...(cols.show("type") ? [{ key: "type", label: t("shared.colType") }] : []),
  ];
  const csvRows = filtered.map((u) => ({ displayName: u.displayName, mail: u.mail || "", type: boxPurpose(u) }));

  return (
    <>
      <PageHeader title={t("shared.title")} subtitle={`${boxes.length} ${t("shared.title").toLowerCase()}`}>
        {manage && (
          <button onClick={() => { setDialogBox(null); setDialogMode("create"); }} data-testid="shared-create-button" className="flex h-10 items-center gap-2 bg-primary px-4 text-sm font-semibold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-[#002480]">
            <Plus size={16} /> {t("shared.create")}
          </button>
        )}
      </PageHeader>
      <div className="p-6 md:p-10">
        {manage && retained.length > 0 && (
          <div className="mb-6 border border-amber-200 bg-amber-50/60" data-testid="retention-panel">
            <div className="flex items-center gap-2 border-b border-amber-200 px-4 py-3">
              <Clock size={16} className="text-amber-600" />
              <span className="text-sm font-bold text-amber-900">Aufbewahrte Postfächer (Austritt)</span>
              <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800">{retained.length}</span>
              <span className="ml-auto hidden text-xs text-amber-700 sm:block">Werden nach Ablauf automatisch gelöscht — verlängern oder reaktivieren</span>
            </div>
            <div className="divide-y divide-amber-100">
              {retained.map((r) => {
                const left = r.daysLeft;
                const badge = left == null ? "border-zinc-200 bg-white text-zinc-500"
                  : left <= 2 ? "border-red-300 bg-red-50 text-red-700"
                  : left <= 7 ? "border-amber-300 bg-amber-100 text-amber-800"
                  : "border-emerald-300 bg-emerald-50 text-emerald-700";
                const reactivating = r.status === "reactivating";
                return (
                  <div key={r.identity} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`retention-row-${r.identity}`}>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-zinc-900">{r.displayName || r.identity}</span>
                      <span className="block truncate font-mono text-xs text-zinc-400">{r.identity}</span>
                    </span>
                    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[11px] font-bold ${badge}`} data-testid={`retention-days-${r.identity}`}>
                      <CalendarClock size={12} />
                      {reactivating ? "Reaktivierung läuft…" : left == null ? "—" : left <= 0 ? "fällig" : `${left} Tag(e)`}
                    </span>
                    {!reactivating && (
                      <div className="flex gap-1">
                        <Button onClick={() => { setExtendFor(r); setExtendDays(30); }} variant="outline" size="sm" data-testid={`retention-extend-${r.identity}`} className="h-8 rounded-sm border-zinc-300 text-xs">
                          <CalendarClock size={13} className="mr-1" /> Verlängern
                        </Button>
                        <Button onClick={() => setReactivateFor(r)} size="sm" data-testid={`retention-reactivate-${r.identity}`} className="h-8 rounded-sm bg-emerald-600 text-xs hover:bg-emerald-700">
                          <RotateCcw size={13} className="mr-1" /> Reaktivieren
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shared.searchPh")} data-testid="shared-search" className="rounded-sm border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "mail", label: t("shared.colEmail") }, { key: "type", label: t("shared.colType") }]} />
            <ExportCsvButton label={t("cols.export")} filename="shared-mailboxes.csv" headers={csvHeaders} rows={csvRows} />
            {isAdmin && (
              <Button onClick={rescan} disabled={rescanning} variant="outline" data-testid="shared-rescan" className="rounded-sm border-zinc-300">
                <RefreshCw size={15} className={`mr-2 ${rescanning ? "animate-spin" : ""}`} /> {rescanning ? t("shared.rescanning") : t("shared.rescan")}
              </Button>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 bg-white">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <SortTh label={t("shared.colMailbox")} sortKey="displayName" sort={sort} />
                {cols.show("mail") && <SortTh label={t("shared.colEmail")} sortKey="mail" sort={sort} className="hidden md:table-cell" />}
                {cols.show("type") && <SortTh label={t("shared.colType")} sortKey="type" sort={sort} filter={{ value: typeFilter, onChange: setTypeFilter, allValue: "all", options: [
                  { value: "all", label: t("shared.typeAll") },
                  { value: "shared", label: t("shared.typeShared") },
                  { value: "room", label: t("shared.typeRoom") },
                  { value: "equipment", label: t("shared.typeEquipment") },
                ] }} />}
                {isAdmin && <SortTh label={t("groups.colVisible")} sortKey="isVisible" sort={sort} filter={{ value: visFilter, onChange: setVisFilter, allValue: "all", options: [
                  { value: "visible", label: t("groups.visVisible") },
                  { value: "hidden", label: t("groups.visHidden") },
                  { value: "all", label: t("groups.visAll") },
                ] }} />}
                {manage && <th className="px-4 py-3 text-right">{t("teams.colActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={colCount} className="px-4 py-10 text-center font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-sm text-zinc-400">{t("shared.empty")}</td></tr>}
              {visible.map((u) => {
                const purpose = (u.userPurpose || "shared").toLowerCase();
                return (
                  <tr key={u.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`shared-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-sky-700 text-xs font-bold text-white">{initials(u.displayName)}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-zinc-900">{u.displayName}</span>
                          <span className="block truncate font-mono text-xs text-zinc-400">{u.userPrincipalName}</span>
                        </span>
                      </div>
                    </td>
                    {cols.show("mail") && <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{u.mail || "—"}</td>}
                    {cols.show("type") && <td className="px-4 py-3"><span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${PURPOSE_CLS[purpose] || PURPOSE_CLS.shared}`}>{t(PURPOSE_LABEL[purpose] || PURPOSE_LABEL.shared)}</span></td>}
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <button onClick={() => toggleVis(u.id, u.isVisible === false)} data-testid={`shared-vis-${u.id}`}
                          title={u.isVisible === false ? t("groups.toggleHidden") : t("groups.toggleVisible")}
                          className={`flex h-8 w-8 items-center justify-center border transition-colors ${u.isVisible === false ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                          {u.isVisible === false ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </td>
                    )}
                    {manage && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => { setDialogBox(u); setDialogMode("edit"); }} data-testid={`shared-edit-${u.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><Pencil size={15} /></button>
                          <button onClick={() => setDeleting(u)} data-testid={`shared-delete-${u.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hasMore && <div ref={sentinelRef} className="flex items-center justify-center py-4 text-xs text-zinc-400"><Loader2 size={14} className="mr-2 animate-spin" /> {shown}/{total}</div>}
        </div>
      </div>

      <SharedMailboxDialog mode={dialogMode} mailbox={dialogBox} domains={domains}
        open={!!dialogMode} onOpenChange={(o) => !o && setDialogMode(null)} onSaved={load} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("shared.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("shared.deleteWarn")} <span className="font-semibold text-zinc-700">{deleting?.displayName}</span> ({deleting?.mail})</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy} data-testid="shared-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("teams.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extend retention */}
      <AlertDialog open={!!extendFor} onOpenChange={(o) => !o && setExtendFor(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Aufbewahrung verlängern</AlertDialogTitle>
            <AlertDialogDescription>
              Verlängert die Aufbewahrung von <span className="font-semibold text-zinc-700">{extendFor?.displayName || extendFor?.identity}</span> um die angegebene Anzahl Tage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Tage</label>
            <Input type="number" min={1} max={3650} value={extendDays} onChange={(e) => setExtendDays(e.target.value)} data-testid="retention-extend-days" className="w-32 rounded-sm border-zinc-300" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doExtend} disabled={busyRet} data-testid="retention-extend-confirm" className="rounded-sm">
              {busyRet ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} Verlängern
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate leaver */}
      <AlertDialog open={!!reactivateFor} onOpenChange={(o) => !o && setReactivateFor(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Postfach reaktivieren</AlertDialogTitle>
            <AlertDialogDescription>
              Wandelt <span className="font-semibold text-zinc-700">{reactivateFor?.displayName || reactivateFor?.identity}</span> zurück in ein reguläres Benutzerpostfach: sichtbar in der GAL, ursprünglicher Name wird wiederhergestellt, Abwesenheitsnotiz aus, Anmeldung aktiviert, geplante Löschung abgebrochen.
              <span className="mt-2 block rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
                Hinweis: Das Konto wird <strong>ohne Lizenz</strong> reaktiviert. Weise dem Benutzer anschließend eine Lizenz zu, damit das Postfach dauerhaft bestehen bleibt.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doReactivate} disabled={busyRet} data-testid="retention-reactivate-confirm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700">
              {busyRet ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} Reaktivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
